import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Container, Grid, Typography, Stack } from "@mui/material";
import {
  AccountBalanceWalletOutlined as WalletIcon,
  TrendingUp as TrendingIcon,
  CasinoOutlined as LotteryIcon,
  PeopleOutline as CreatorsIcon,
  HandshakeOutlined as PartnersIcon,
  EmojiEventsOutlined as WinsIcon,
  ConfirmationNumberOutlined as TicketIcon,
  ReceiptLongOutlined as DrawIcon,
  AttachMoneyOutlined as MoneyIcon,
  AccountBalanceOutlined as BankIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Web3 Contract Services
import cinstance from "../../Service/randomcinstance";
import usercinstance from "../../Service/usercinstance";
import web3 from "../../Service/web3";
import { networkID } from "../../../static/utils";
import { colors, customShadows, borderRadiusPx } from "../../../theme";
import { Path } from "../../../static/Constants";

// Dashboard MUI Components
import {
  AdminLayout,
  MetricCard,
  DashboardNoticeBanner,
} from "../../Components/dashboard";

import {
  CommonButton,
  CommonDialog,
  CommonSelect,
  AccessDeniedScreen,
} from "../../Components/ui";

import "./AdminHome.css";

const withTimeout = (promise, ms = 3500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms)
    ),
  ]);
};

const AdminHome = () => {
  const history = useHistory();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminAddress, setAdminAddress] = useState("");
  const [warn, setWarn] = useState(false);
  const [net, setNet] = useState(false);

  // Original Data Fields
  const [totalLottery, setTotalLottery] = useState(0);
  const [totalTicketSold, setTotalTicketSold] = useState(0);
  const [totalOrganisation, setTotalOrganisation] = useState(0);
  const [commissionEarned, setCommissionEarned] = useState(0);
  const [totalPartners, setTotalPartners] = useState(0);
  const [totalDraws, setTotalDraws] = useState(0);
  const [totalWins, setTotalWins] = useState(0);
  const [regFee, setRegFee] = useState(0);
  const [lotteryFee, setLotteryFee] = useState(0);
  const [winnerTax, setWinnerTax] = useState(0);
  const [payoutPartner, setPayoutPartner] = useState(0);
  const [payoutAdmin, setPayoutAdmin] = useState(0);
  const [payoutWinner, setPayoutWinner] = useState(0);

  // Filter & UI State
  const [dateRange, setDateRange] = useState("All-Time Historical");

  // Modals
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const safetyTimer = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 2000);

    const initData = async () => {
      try {
        if (!window.ethereum && !web3) {
          if (isMounted) setLoading(false);
          return;
        }

        if (window.ethereum) {
          window.web3 = web3;

          try {
            const netId = await withTimeout(web3.eth.net.getId(), 2500);
            if (netId !== networkID && Number(netId) !== Number(networkID)) {
              if (isMounted) {
                setWarn(true);
                setNet(true);
              }
            }
          } catch (netErr) {
            console.warn("Network check skipped/timed out:", netErr);
          }

          let accounts = [];
          try {
            if (window.ethereum.selectedAddress) {
              accounts = [window.ethereum.selectedAddress];
            } else if (window.ethereum.request) {
              accounts = await withTimeout(
                window.ethereum.request({ method: "eth_accounts" }),
                2000
              );
            }
            if (!accounts || accounts.length === 0) {
              accounts = await withTimeout(web3.eth.getAccounts(), 2000);
            }
          } catch (accErr) {
            console.warn("Account retrieval skipped:", accErr);
          }

          const currentAccount = accounts?.[0] || "";
          if (isMounted) setAdminAddress(currentAccount);

          let p = "";
          try {
            p = await withTimeout(cinstance.methods.admin().call(), 3000);
          } catch (e) {
            console.warn("Could not fetch contract admin:", e);
          }

          if (currentAccount && (p === currentAccount || p.toLowerCase() === currentAccount.toLowerCase())) {
            if (isMounted) setShow(true);
          }

          const adminTarget = p || currentAccount;

          if (adminTarget) {
            const tasks = [
              // 1. Total Lotteries
              (async () => {
                try {
                  const res2 = await withTimeout(cinstance.methods.lotteryId().call(), 3000);
                  if (isMounted && res2) {
                    setTotalLottery(res2 > 0 ? Number(res2) - 1 : 0);
                  }
                } catch (e) {
                  console.warn("Error fetching lotteryId:", e);
                }
              })(),

              // 2. Total Organisations / Creators
              (async () => {
                try {
                  const res3 = await withTimeout(usercinstance.methods.ownerId().call(), 3000);
                  if (isMounted && res3) {
                    setTotalOrganisation(res3 > 0 ? Number(res3) - 1 : 0);
                  }
                } catch (e) {
                  console.warn("Error fetching ownerId:", e);
                }
              })(),

              // 3. Total Partners
              (async () => {
                try {
                  const res4 = await withTimeout(cinstance.methods.partnerId().call(), 3000);
                  if (isMounted && res4 !== undefined) {
                    setTotalPartners(Number(res4) || 0);
                  }
                } catch (e) {
                  console.warn("Error fetching partnerId:", e);
                }
              })(),

              // 4. Total Draws
              (async () => {
                try {
                  const res5 = await withTimeout(cinstance.methods.totalDraws().call(), 3000);
                  if (isMounted && res5 !== undefined) {
                    setTotalDraws(Number(res5) || 0);
                  }
                } catch (e) {
                  console.warn("Error fetching totalDraws:", e);
                }
              })(),

              // 5. Total Wins
              (async () => {
                try {
                  const res6 = await withTimeout(cinstance.methods.totalWinners().call(), 3000);
                  if (isMounted && res6 !== undefined) {
                    setTotalWins(Number(res6) || 0);
                  }
                } catch (e) {
                  console.warn("Error fetching totalWinners:", e);
                }
              })(),

              // 6. Total Partner Pay
              (async () => {
                try {
                  const res7 = await withTimeout(cinstance.methods.totalPartnerPay().call(), 3000);
                  if (isMounted && res7 !== undefined) {
                    setPayoutPartner(res7);
                  }
                } catch (e) {
                  console.warn("Error fetching totalPartnerPay:", e);
                }
              })(),

              // 7. Total Commission Earned
              (async () => {
                try {
                  const res8 = await withTimeout(cinstance.methods.totalCommissionEarned().call(), 3000);
                  if (isMounted && res8 !== undefined) {
                    setPayoutAdmin(res8);
                  }
                } catch (e) {
                  console.warn("Error fetching totalCommissionEarned:", e);
                }
              })(),

              // 8. Registration Fees
              (async () => {
                try {
                  const res9 = await withTimeout(usercinstance.methods.registrationFees(adminTarget).call(), 3000);
                  if (isMounted && res9 !== undefined) {
                    setRegFee(res9);
                  }
                } catch (e) {
                  console.warn("Error fetching registrationFees:", e);
                }
              })(),

              // 9. Lottery Creation Fees
              (async () => {
                try {
                  const res10 = await withTimeout(cinstance.methods.totalLotteryCreationFees(adminTarget).call(), 3000);
                  if (isMounted && res10 !== undefined) {
                    setLotteryFee(res10);
                  }
                } catch (e) {
                  console.warn("Error fetching totalLotteryCreationFees:", e);
                }
              })(),

              // 10. Winner Tax
              (async () => {
                try {
                  const res11 = await withTimeout(cinstance.methods.winnerTax(adminTarget).call(), 3000);
                  if (isMounted && res11 !== undefined) {
                    setWinnerTax(res11);
                  }
                } catch (e) {
                  console.warn("Error fetching winnerTax:", e);
                }
              })(),

              // 11. Total Sale
              (async () => {
                try {
                  const res12 = await withTimeout(cinstance.methods.totalSale().call(), 3000);
                  if (isMounted && res12 !== undefined) {
                    setTotalTicketSold(Number(res12) || 0);
                  }
                } catch (e) {
                  console.warn("Error fetching totalSale:", e);
                }
              })(),

              // 12. Winner Amount
              (async () => {
                try {
                  const res13 = await withTimeout(cinstance.methods.totalWinnerAmount(adminTarget).call(), 3000);
                  if (isMounted && res13 !== undefined) {
                    setPayoutWinner(res13);
                  }
                } catch (e) {
                  console.warn("Error fetching totalWinnerAmount:", e);
                }
              })(),
            ];

            await Promise.allSettled(tasks);

            if (isMounted) {
              const regFeeNum = Number(regFee) || 0;
              const lotteryFeeNum = Number(lotteryFee) || 0;
              const winnerTaxNum = Number(winnerTax) || 0;
              setCommissionEarned(regFeeNum + lotteryFeeNum + winnerTaxNum);
            }
          }
        }
      } catch (err) {
        console.error("Admin dashboard initialization error:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initData();

    const handleAccountsChanged = (newAccounts) => {
      if (isMounted) {
        if (newAccounts && newAccounts.length > 0) {
          setAdminAddress(newAccounts[0]);
          initData();
        } else {
          setAdminAddress("");
          setShow(false);
          setLoading(false);
        }
      }
    };

    if (window.ethereum?.on) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
    }

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      if (window.ethereum?.removeListener) {
        window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      }
    };
  }, [regFee, lotteryFee, winnerTax]);

  // Handle Admin Withdraw (Exact Smart Contract Logic)
  const handleWithdraw = useCallback(async () => {
    try {
      setWithdrawing(true);
      const accounts = await web3.eth.getAccounts();
      if (!accounts?.[0]) {
        toast.error("No connected admin wallet found.");
        setWithdrawing(false);
        return;
      }

      await cinstance.methods.withdrawAdmin().send({ from: accounts[0] });
      await usercinstance.methods.withdrawRegisterationFees().send({ from: accounts[0] });

      toast.success("Platform earnings withdrawn successfully!");
      setWithdrawModalOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("Admin withdraw error:", err);
      toast.error("Withdrawal was cancelled or encountered an error.");
    } finally {
      setWithdrawing(false);
    }
  }, []);

  // Formatted numeric values for POL displays
  const commissionEarnedFormatted = useMemo(() => {
    return commissionEarned ? (Number(commissionEarned) / 10 ** 18).toFixed(5) : "0.00000";
  }, [commissionEarned]);

  const regFeeFormatted = useMemo(() => {
    return regFee ? (Number(regFee) / 10 ** 18).toFixed(5) : "0.00000";
  }, [regFee]);

  const lotteryFeeFormatted = useMemo(() => {
    return lotteryFee ? (Number(lotteryFee) / 10 ** 18).toFixed(5) : "0.00000";
  }, [lotteryFee]);

  const winnerTaxFormatted = useMemo(() => {
    return winnerTax ? (Number(winnerTax) / 10 ** 18).toFixed(5) : "0.00000";
  }, [winnerTax]);

  const payoutWinnerFormatted = useMemo(() => {
    return payoutWinner ? (Number(payoutWinner) / 10 ** 18).toFixed(5) : "0.00000";
  }, [payoutWinner]);

  const payoutPartnerFormatted = useMemo(() => {
    return payoutPartner ? (Number(payoutPartner) / 10 ** 18).toFixed(5) : "0.00000";
  }, [payoutPartner]);

  if (warn === true) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background.default,
          py: 8,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: borderRadiusPx.xxl,
              backgroundColor: colors.background.paper,
              boxShadow: customShadows.dialog,
              border: `1px solid ${colors.dashboard.cardBorder}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: colors.slate[900], mb: 2 }}
            >
              Network Connection Required
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.slate[500], mb: 3 }}
            >
              {net
                ? `Please connect to the correct network (Network ID: ${networkID}) in your Web3 wallet.`
                : "Please install an Ethereum-compatible browser extension like MetaMask to access the Admin Console."}
            </Typography>
            <CommonButton
              variant="contained"
              onClick={() => history.push(Path.help)}
            >
              Get Help
            </CommonButton>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!show && !loading) {
    return (
      <AccessDeniedScreen
        title="Not An Admin"
        walletAddress={adminAddress}
        roleName="administrator"
      />
    );
  }

  return (
    <AdminLayout
      title="Admin Dashboard"
      subtitle="Overview of platform earnings, system activity, and payouts"
      breadcrumbs={[{ label: "Dashboard" }]}
      headerType="dashboard"
      dateRangeText={dateRange}
      onDateRangeChange={(range) => setDateRange(range)}
      onFilterClick={() => setFilterModalOpen(true)}
      creatorAddress={adminAddress}
    >
      <Stack spacing={3.5}>
        {/* Platform Earned Hero Card with Withdraw Button */}
            <Box
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: borderRadiusPx.card,
                backgroundColor: colors.dashboard.cardBg,
                border: `1px solid ${colors.dashboard.cardBorder}`,
                boxShadow: customShadows.cardSubtle,
              }}
            >
              <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid item xs={12} sm={8}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.dashboard.subtitle,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Platform Earned
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: colors.dashboard.title,
                      letterSpacing: "-0.02em",
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                    }}
                  >
                    {commissionEarnedFormatted}
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{ color: colors.primary.main, fontWeight: 700 }}
                    >
                      POL
                    </Typography>
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <CommonButton
                    variant="primary"
                    size="large"
                    onClick={() => setWithdrawModalOpen(true)}
                  >
                    Withdraw
                  </CommonButton>
                </Grid>
              </Grid>
            </Box>

            {/* SECTION 1: Platform Earnings (3 Cards) */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: colors.dashboard.title,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MoneyIcon sx={{ color: colors.emerald[600], fontSize: "1.3rem" }} />
                Platform Earnings
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    title="Registration Fees"
                    value={`${regFeeFormatted} POL`}
                    trend={{ value: "On-Chain", isPositive: true, label: "Creator onboarding" }}
                    tooltip="Registration fees collected from onboarded creators."
                    chartColor={colors.charts.blue}
                    chartData={[10, 14, 18, 22, 26, 30, 35, 40, 45]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    title="Lottery Creation Fees"
                    value={`${lotteryFeeFormatted} POL`}
                    trend={{ value: "On-Chain", isPositive: true, label: "Campaign deployments" }}
                    tooltip="Creation fees paid by creators when publishing lotteries."
                    chartColor={colors.charts.green}
                    chartData={[15, 20, 24, 28, 32, 38, 44, 50, 56]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    title="Winner’s Tax"
                    value={`${winnerTaxFormatted} POL`}
                    trend={{ value: "On-Chain", isPositive: true, label: "Prize distributions" }}
                    tooltip="Statutory winner tax collected from game payout settlements."
                    chartColor={colors.charts.purple}
                    chartData={[12, 16, 22, 25, 30, 36, 42, 48, 54]}
                    loading={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* SECTION 2: Platform Activity (6 Cards) */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: colors.dashboard.title,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TrendingIcon sx={{ color: colors.blue[600], fontSize: "1.3rem" }} />
                Platform Activity
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Total Creators"
                    value={totalOrganisation || 0}
                    trend={{ value: "Operators", isPositive: true, label: "Registered organizers" }}
                    tooltip="Total approved organisation/creator accounts."
                    chartColor={colors.charts.blue}
                    chartData={[4, 6, 8, 11, 14, 16, 20, 24, 28]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Total Lotteries"
                    value={totalLottery || 0}
                    trend={{ value: "Campaigns", isPositive: true, label: "Deployed lotteries" }}
                    tooltip="Total lottery games created on AutoBET."
                    chartColor={colors.charts.green}
                    chartData={[8, 12, 16, 20, 25, 30, 35, 40, 48]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Total Ticket Sold"
                    value={totalTicketSold || 0}
                    trend={{ value: "Entries", isPositive: true, label: "Player tickets bought" }}
                    tooltip="Total tickets purchased across all games."
                    chartColor={colors.charts.cyan}
                    chartData={[50, 75, 100, 130, 170, 220, 280, 350, 420]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Total Draws"
                    value={totalDraws || 0}
                    trend={{ value: "Rounds", isPositive: true, label: "Executed draws" }}
                    tooltip="Total lottery draw rounds settled on-chain."
                    chartColor={colors.charts.purple}
                    chartData={[6, 9, 12, 15, 18, 22, 26, 31, 36]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Total Wins"
                    value={totalWins || 0}
                    trend={{ value: "Winners", isPositive: true, label: "Rewarded participants" }}
                    tooltip="Total winners generated across all lotteries."
                    chartColor={colors.charts.orange || colors.charts.blue}
                    chartData={[3, 5, 7, 10, 12, 15, 18, 21, 25]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Total Partners"
                    value={totalPartners || 0}
                    trend={{ value: "Network", isPositive: true, label: "Registered affiliates" }}
                    tooltip="Total partners participating in revenue sharing."
                    chartColor={colors.charts.cyan}
                    chartData={[2, 3, 4, 6, 7, 9, 10, 12, 15]}
                    loading={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* SECTION 3: Payout (3 Cards) */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: colors.dashboard.title,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BankIcon sx={{ color: colors.purple[600], fontSize: "1.3rem" }} />
                Payout
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    title="Winner’s"
                    value={`${payoutWinnerFormatted} POL`}
                    trend={{ value: "Distributed", isPositive: true, label: "Prizes paid to players" }}
                    tooltip="Total prize value paid out to lottery winners."
                    chartColor={colors.charts.green}
                    chartData={[20, 30, 45, 60, 80, 105, 130, 160, 190]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    title="Partners"
                    value={`${payoutPartnerFormatted} POL`}
                    trend={{ value: "Distributed", isPositive: true, label: "Partner revenue shares" }}
                    tooltip="Total revenue shares distributed to ecosystem partners."
                    chartColor={colors.charts.cyan}
                    chartData={[10, 15, 22, 30, 40, 52, 65, 80, 95]}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    title="Admin"
                    value={`${commissionEarnedFormatted} POL`}
                    trend={{ value: "Claimable", isPositive: true, label: "Admin platform revenue" }}
                    tooltip="Total net platform commissions accrued to the admin."
                    chartColor={colors.charts.purple}
                    chartData={[15, 20, 25, 32, 40, 50, 62, 75, 90]}
                    loading={loading}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>

      {/* Filter Modal Dialog */}
      <CommonDialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Admin Dashboard Filters"
        maxWidth="xs"
        actions={
          <>
            <CommonButton
              variant="reset"
              onClick={() => setFilterModalOpen(false)}
            >
              Reset
            </CommonButton>
            <CommonButton
              variant="contained"
              onClick={() => {
                setFilterModalOpen(false);
                toast.info("Filters applied!");
              }}
            >
              Apply Filters
            </CommonButton>
          </>
        }
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <CommonSelect
            label="Filter Period"
            value="all"
            onChange={() => {}}
            options={[
              { value: "all", label: "All-Time Historical" },
              { value: "month", label: "Current Month" },
              { value: "quarter", label: "Current Quarter" },
            ]}
          />
        </Stack>
      </CommonDialog>

      {/* Withdrawal Confirmation Dialog */}
      <CommonDialog
        open={withdrawModalOpen}
        onClose={() => !withdrawing && setWithdrawModalOpen(false)}
        title="Withdraw Platform Earnings"
        maxWidth="xs"
        actions={
          <>
            <CommonButton
              variant="cancel"
              onClick={() => setWithdrawModalOpen(false)}
              disabled={withdrawing}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              onClick={handleWithdraw}
              disabled={withdrawing}
            >
              {withdrawing ? "Processing..." : "Confirm Withdrawal"}
            </CommonButton>
          </>
        }
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: colors.slate[600] }}>
            You are about to withdraw all accumulated platform earnings and registration fees to your connected admin wallet address:
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.md,
              backgroundColor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[500], display: "block" }}>
              Total Claimable Earnings:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.purple[700] }}>
              {commissionEarnedFormatted} POL
            </Typography>
            <Typography variant="caption" sx={{ color: colors.slate[500], display: "block", mt: 1 }}>
              Destination Admin Address:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all", color: colors.slate[800] }}>
              {adminAddress}
            </Typography>
          </Box>
        </Stack>
      </CommonDialog>
    </AdminLayout>
  );
};

export default AdminHome;

